"use client";

import React, { useMemo } from "react";

import { cn } from "utils";
import { deviceDetector } from "@ospweb/sdk-ui";
import { Asset } from "../DistributionCard/Asset";
import { DistributionCard } from "../DistributionCard";
import { PerformanceCard } from "../PerformanceCard";
import { openInsightModal } from "../InsightModal";
import { InsightPanelProps } from "./types";
import { useHistory } from "./useHistory";
import { InsightPanelSkeleton } from "./InsightPanelSkeleton";
import Translations from "@translate/components/Portfolio/AssetInsight/InsightPanel/InsightPanel";
import { I18nUtil } from "@utils/i18n";
import { AppStore } from "@/src/store/modules/app";

const I18n = I18nUtil.createScoped(Translations);

/**
 * @description Portfolio 资产洞察桌面端容器，负责组合性能与分布子模块。
 */
export const InsightPanel: React.FC<InsightPanelProps> = ({
  className,
  data,
  accountId,
  value,
  topAssets,
  assetTypes,
  onViewAll,
  isExternal = false,
  isLoading = false,
}) => {
  const { history, setPeriod, period, isFetching } = useHistory({
    id: accountId,
    value,
  });

  const onClose = () => {
    if (deviceDetector.isMobile() || deviceDetector.isH5()) {
      AppStore.portfolioChartVisabled = false;
    }
  };

  const onViewAllClick = () => {
    if (onViewAll) {
      return onViewAll();
    }
    openInsightModal({
      viewName: "all",
      accountId,
      data,
      topAssets,
      assetTypes,
      value,
      onClose,
    });
  };
  const isBlock = useMemo(() => {
    const isTopAssets = topAssets?.length && topAssets?.length > 0;
    const isHistory =
      history?.valueData?.length && history?.valueData?.length > 1;
    return isTopAssets || isHistory;
  }, [topAssets, history]);

  if (isLoading) {
    return <InsightPanelSkeleton className={className} />;
  }

  // 外部导入类型的布局：左侧History，右侧Asset
  if (isExternal) {
    return (
      <section
        aria-label="Insight Panel section"
        data-openreplay-label="Insight Panel section Semantic"
        id="sem-insight-panel-section"
        className={cn(
          "flex flex-col gap-3 box-border",
          "md:flex-row md:gap-8 md:px-5 md:pb-5 md:pt-3",
          className,
        )}
      >
        <DistributionCard
          className="flex-1 min-h-91 md:min-h-auto"
          history={history}
          onViewAll={() => onViewAllClick()}
          setPeriod={setPeriod}
          period={period}
          topAssets={topAssets}
          assetTypes={assetTypes}
          isFetching={isFetching}
          startTime={data?.dataValue?.[0]?.timestamp}
          hideAssetTypes={true}
          hideAsset={true}
        />
        <div
          className={cn("hidden md:self-stretch md:w-px md:bg-border_solid", {
            block: isBlock,
          })}
        />
        <article
          aria-label="Insight Panel article"
          data-openreplay-label="Insight Panel article Semantic"
          id="sem-insight-panel-article"
          className="flex-1 min-h-91 md:min-h-auto flex flex-col gap-3 px-5 pb-5 pt-3 md:p-0"
        >
          <header
            aria-label="Insight Panel header"
            data-openreplay-label="Insight Panel header Semantic"
            id="sem-insight-panel-header"
            className="flex items-center"
          >
            <span className="body_medium_m text-content_primary">
              {I18n.t("Asset")}
            </span>
          </header>
          <Asset
            onViewall={() => onViewAllClick()}
            className="flex-1"
            topAssets={topAssets}
          />
        </article>
      </section>
    );
  }

  // 默认布局：左侧Performance，右侧DistributionCard
  return (
    <section
      aria-label="Insight Panel section"
      data-openreplay-label="Insight Panel section Semantic"
      id="sem-insight-panel-section-2"
      className={cn(
        "flex flex-col gap-3 box-border",
        "md:flex-row md:gap-8 md:px-5 md:pb-5 md:pt-3",
        className,
      )}
    >
      <PerformanceCard className="flex-1" data={data} />
      <div
        className={cn("hidden md:self-stretch md:w-px md:bg-border_solid", {
          block: isBlock,
        })}
      />
      <DistributionCard
        className="flex-1 min-h-91 md:min-h-auto"
        history={history}
        onViewAll={() => onViewAllClick()}
        setPeriod={setPeriod}
        period={period}
        topAssets={topAssets}
        assetTypes={assetTypes}
        isFetching={isFetching}
        startTime={data?.dataValue?.[0]?.timestamp}
      />
    </section>
  );
};
